// The one registration form both `/daftar` paths render, parameterised by `path` so `ONBOARDING_
// COPY` (DESIGN.md §10.4, AC-11) decides every word on screen — this component itself types no
// Indonesian sentence of its own beyond the field labels shared by both paths ("Email", "Kata
// sandi", "Konfirmasi kata sandi") and the mismatch error, none of which appears on either ban list. Exactly one `auth_register` request per
// submission (api-contract.yaml `RegisterRequest`): both the `account` and `data_processing`
// consents are required to register, so the submit button stays disabled until both are checked
// and the two password fields match.
'use client';

import { useId, useState, type FormEvent } from 'react';
// Relative, not `@/` — `frontend/vitest.config.ts` (T-03's, frozen) has no path-alias resolution.
import { Checkbox } from '../../../components/ui/Checkbox';
import { Field } from '../../../components/ui/Field';
import { Input } from '../../../components/ui/Input';
import { NoticeBand } from '../../../components/ui/NoticeBand';
import { PasswordInput } from '../../../components/ui/PasswordInput';
import { getApiError, registerAccount, useInvalidateSession, type AuthenticatedUser } from '../../auth';
import { CONSENT_TEXT_VERSION, ONBOARDING_COPY, type OnboardingPath } from '../copy';

export interface RegisterFormProps {
  // Selects the copy. Only `urun` can be submitted (see `handleSubmit`).
  path: OnboardingPath;
}

type Status = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'error'; message: string };

const GENERIC_ERROR = 'Pendaftaran tidak berhasil. Periksa kembali data Anda dan coba lagi.';

export function RegisterForm({ path }: RegisterFormProps) {
  const copy = ONBOARDING_COPY[path];
  const nameId = useId();
  const emailId = useId();
  const passwordId = useId();
  const confirmId = useId();
  const phoneId = useId();
  const accountConsentId = useId();
  const dataConsentId = useId();
  const invalidateSession = useInvalidateSession();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [phone, setPhone] = useState('');
  const [accountConsent, setAccountConsent] = useState(false);
  const [dataConsent, setDataConsent] = useState(false);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [created, setCreated] = useState<AuthenticatedUser | undefined>();

  const consentsGranted = accountConsent && dataConsent;
  const passwordsMatch = password === confirmPassword;
  // Shown once the visitor has left the confirmation field (or tried to submit), not on every
  // keystroke while they are still typing it.
  const confirmError =
    confirmTouched && confirmPassword !== '' && !passwordsMatch ? 'Kata sandi tidak sama.' : undefined;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!consentsGranted) {
      return;
    }
    if (!passwordsMatch) {
      setConfirmTouched(true);
      return;
    }
    // T-178: `RegisterRequest.path` admits exactly `urun` (api-contract.yaml, CR-008, AC-116). The
    // `rumah` copy is dormant and kept for restoration (DESIGN.md §10.4), so this form may render it
    // but never sends it: no `auth_register` request is made for any other path.
    if (path !== 'urun') {
      return;
    }
    setStatus({ kind: 'submitting' });
    const { data, error } = await registerAccount({
      path,
      email,
      password,
      full_name: fullName,
      ...(copy.phoneLabel && phone ? { phone } : {}),
      consents: [
        { purpose: 'account', granted: true, text_version: CONSENT_TEXT_VERSION },
        { purpose: 'data_processing', granted: true, text_version: CONSENT_TEXT_VERSION },
      ],
    });
    if (error) {
      const apiError = getApiError(error);
      setStatus({ kind: 'error', message: apiError?.message ?? GENERIC_ERROR });
      return;
    }
    if (!data) {
      setStatus({ kind: 'error', message: GENERIC_ERROR });
      return;
    }
    setStatus({ kind: 'idle' });
    setCreated(data);
    // Registration already set the session cookies (api-contract.yaml `auth_register` 201): any
    // `useSession()` consumer the visitor navigates to next (e.g. `(akun)`) should see the fresh
    // account rather than its cached "signed out" state.
    invalidateSession();
  }

  if (created) {
    return (
      <NoticeBand tone="settled" title={copy.successHeading}>
        {copy.successMessage}
      </NoticeBand>
    );
  }

  const submitting = status.kind === 'submitting';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-s5" noValidate>
      {status.kind === 'error' ? (
        <NoticeBand tone="attention" title="Pendaftaran tidak berhasil">
          {status.message}
        </NoticeBand>
      ) : null}

      <Field id={nameId} label={copy.fullNameLabel} hint={copy.fullNameHint} required>
        <Input
          id={nameId}
          name="full_name"
          autoComplete="name"
          required
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
        />
      </Field>

      <Field id={emailId} label="Email" required>
        <Input
          id={emailId}
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>

      {copy.phoneLabel ? (
        <Field id={phoneId} label={copy.phoneLabel}>
          <Input
            id={phoneId}
            name="phone"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </Field>
      ) : null}

      <Field id={passwordId} label="Kata sandi" required hint="Minimal 8 karakter.">
        <PasswordInput
          id={passwordId}
          name="password"
          autoComplete="new-password"
          minLength={8}
          required
          aria-describedby={`${passwordId}-hint`}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>

      <Field id={confirmId} label="Konfirmasi kata sandi" required error={confirmError}>
        <PasswordInput
          id={confirmId}
          name="password_confirmation"
          autoComplete="new-password"
          required
          invalid={Boolean(confirmError)}
          aria-describedby={confirmError ? `${confirmId}-error` : undefined}
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          onBlur={() => setConfirmTouched(true)}
        />
      </Field>

      <div className="flex flex-col gap-s2">
        <Checkbox
          id={accountConsentId}
          label={copy.accountConsentLabel}
          required
          checked={accountConsent}
          onChange={(event) => setAccountConsent(event.target.checked)}
        />
        <Checkbox
          id={dataConsentId}
          label={copy.dataConsentLabel}
          required
          checked={dataConsent}
          onChange={(event) => setDataConsent(event.target.checked)}
        />
      </div>

      {/* The register Primary (DESIGN-VISUAL.md §16.10, §6.8's "Primary (public register)"
          variant): clay fill, paper label, no border — never `components/ui/Button`'s ink-
          bordered turmeric primary, so this screen never shows both at once. */}
      <button
        type="submit"
        disabled={submitting || !consentsGranted || !password || !passwordsMatch}
        aria-busy={submitting || undefined}
        className="inline-flex w-full min-h-tap-min min-w-tap-min items-center justify-center gap-s2 rounded-control border-0 bg-clay px-s5 py-s2 text-body font-label text-paper transition-colors duration-instant ease-standard disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? 'Memproses…' : copy.submitLabel}
      </button>
    </form>
  );
}
